import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Save, Eye, EyeOff, Loader2, Info, Navigation, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const FmsSettings = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [showToken, setShowToken]   = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);

  const [form, setForm] = useState({
    fms_token: '',
    fms_url:   'https://fms.locanix.net/webservicescript/103/SingleVehicle',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${backendUrl}/api/fms/settings`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setForm({
            fms_token: data.fms_token || '',
            fms_url:   data.fms_url   || 'https://fms.locanix.net/webservicescript/103/SingleVehicle',
          });
          setTokenSaved(data.token_saved || false);
        }
      })
      .catch(() => toast.error('Failed to load FMS settings'))
      .finally(() => setLoading(false));
  }, [backendUrl, getHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/fms/settings`, {
        method:  'PUT',
        headers: getHeaders(),
        body:    JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('FMS settings saved successfully');
      if (form.fms_token && form.fms_token !== '***') setTokenSaved(true);
    } catch {
      toast.error('Failed to save FMS settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl" data-testid="fms-settings-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <Navigation className="w-5 h-5 text-accent-blue" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">FMS Settings</h2>
          <p className="text-sm text-text-secondary font-ibm mt-0.5">
            Configure Locanix Fleet Management System for live vehicle tracking
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 font-ibm leading-relaxed">
          These settings override the server environment variables. The token is stored encrypted in the database.
          Changes take effect immediately without restarting the server.
          Contact Locanix to whitelist your server&apos;s IP if you see &quot;Access denied&quot; errors.
        </p>
      </div>

      {/* API Token */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2 flex items-center gap-2">
          Locanix API Token
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-ibm font-normal">Encrypted</span>
        </h3>

        <div className="space-y-1.5">
          <Label className="text-text-dark font-ibm font-medium text-sm">Token</Label>
          <div className="relative">
            <Input
              data-testid="fms-token-input"
              type={showToken ? 'text' : 'password'}
              placeholder={tokenSaved ? 'Token saved — enter new value to update' : 'Paste your Locanix API token here'}
              value={form.fms_token === '***' ? '' : form.fms_token}
              onChange={e => set('fms_token', e.target.value)}
              className="pr-10 font-mono text-sm border-gray-200"
            />
            <button
              type="button"
              onClick={() => setShowToken(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-dark"
            >
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {tokenSaved && (
            <div className="flex items-center gap-1.5 text-xs text-green-700 font-ibm">
              <CheckCircle className="w-3.5 h-3.5" />
              Token is saved and active. Leave blank to keep existing value.
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-text-dark font-ibm font-medium text-sm">FMS API URL</Label>
          <Input
            data-testid="fms-url-input"
            type="text"
            value={form.fms_url}
            onChange={e => set('fms_url', e.target.value)}
            className="font-mono text-sm border-gray-200"
            placeholder="https://fms.locanix.net/webservicescript/103/SingleVehicle"
          />
          <p className="text-xs text-text-secondary font-ibm">
            Default: <code className="bg-gray-100 px-1 rounded">https://fms.locanix.net/webservicescript/103/SingleVehicle</code>
          </p>
        </div>
      </Card>

      {/* How it works */}
      <Card className="bg-gray-50 border border-gray-200 p-5 space-y-3">
        <h3 className="font-semibold text-text-dark font-ibm text-sm">How the tracking API call works</h3>
        <div className="font-mono text-xs bg-white border border-gray-200 rounded-lg p-3 text-text-secondary break-all">
          {form.fms_url || 'https://fms.locanix.net/...'}<br />
          <span className="text-accent-blue">?token=</span><span className="text-green-600">[your token]</span>
          <span className="text-accent-blue">&amp;name=</span><span className="text-orange-600">GJ01XX0000/22FT/REF</span>
        </div>
        <p className="text-xs text-text-secondary font-ibm">
          The vehicle name format is auto-generated from the registration number, size, and type
          stored in Fleet Management.
        </p>
      </Card>

      {/* Troubleshooting */}
      <Card className="bg-amber-50 border border-amber-200 p-5 space-y-2">
        <h3 className="font-semibold text-amber-800 font-ibm text-sm flex items-center gap-2">
          <Info className="w-4 h-4" />
          Getting &quot;Access denied&quot;?
        </h3>
        <ol className="text-xs text-amber-800 font-ibm space-y-1 list-decimal list-inside leading-relaxed">
          <li>Verify your token is correct above</li>
          <li>Find your server IP: run <code className="bg-amber-100 px-1 rounded">curl ifconfig.me</code> on your VPS</li>
          <li>Contact Locanix support and request they whitelist your server IP for this token</li>
          <li>This is a one-time setup — once whitelisted, tracking will work from your server</li>
        </ol>
      </Card>

      <Button
        data-testid="fms-save-btn"
        onClick={handleSave}
        disabled={saving}
        className="bg-accent-blue hover:bg-blue-700 text-white font-ibm px-8"
      >
        {saving
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
          : <><Save className="w-4 h-4 mr-2" />Save FMS Settings</>}
      </Button>

    </div>
  );
};

export default FmsSettings;
